import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import '../index.css';
import { useAuth } from '../features/auth/hooks/useAuth';
import { cleanupAuthListener } from '../features/auth/services/sessionService';
import { useDispatch } from 'react-redux';
import { rewardsSliceAPI } from '../features/rewards/store/rewardsSlice';
import { settingsSliceAPI } from '../features/settings/store/settingsSlice';
import { customersSliceAPI } from '../features/customers/store/customersSlice';
import { resetTutorial } from '../features/tutorial/store/tutorialSlice';

// Import all page components
import Dashboard from '../shared/components/layout/DashboardLayout';
import RequirePermission from '../shared/components/RequirePermission';
import Home from '../features/landing/pages/Home';
import PrivacyPolicy from '../features/landing/pages/PrivacyPolicy';
import TermsOfService from '../features/landing/pages/TermsOfService';
import RefundPolicy from '../features/landing/pages/RefundPolicy';
import DashboardHomeRedirect from '../features/admin/pages/DashboardHomeRedirect';
import LoyalPassAdminLayout from '../features/admin/pages/LoyalPassAdminLayout';
import LoyalPassAdminMerchantsPage from '../features/admin/pages/LoyalPassAdminMerchantsPage';
import LoyalPassAdminDashboardPreviewsIndex from '../features/admin/pages/LoyalPassAdminDashboardPreviewsIndex';
import LoyalPassAdminDashboardPreviewPage from '../features/admin/pages/LoyalPassAdminDashboardPreviewPage';
import LoyalPassAdminInviteMerchantPage from '../features/admin/pages/LoyalPassAdminInviteMerchantPage';
import LoyalPassAdminMerchantTierPage from '../features/admin/pages/LoyalPassAdminMerchantTierPage';
import Generate from '../features/generate/pages/GeneratePage';
import Scan from '../features/scan/pages/ScanPage';
import Customers from '../features/customers/pages/CustomersPage';
import Rewards from '../features/rewards/pages/RewardsPage';
import SettingsLayout from '../features/settings/components/SettingsLayout';
import AndroidPassSettings from '../features/settings/components/AndroidPassSettings';
import ApplePassSettings from '../features/settings/components/ApplePassSettings';
import RewardsSettings from '../features/settings/components/RewardsSettings';
import GeneralSettings from '../features/settings/components/GeneralSettings';
import CustomerForm from '../features/customers/components/CustomerForm';
import Marketing from '../features/marketing/pages/MarketingPage';
import OnlinePresence from '../features/online-presence/pages/OnlinePresencePage';
import Membership from '../features/settings/components/MembershipSettings';
import StaffLayout from '../features/staff/components/StaffLayout';
import StaffIndex from '../features/staff/components/StaffIndex';
import RolesTab from '../features/staff/components/RolesTab';
import Login from '../features/auth/pages/Login';
import Register from '../features/auth/pages/Register';
import Confirm from '../features/auth/pages/Confirm';
import ResetPassword from '../features/auth/pages/Reset-password';
import UpdatePassword from '../features/auth/pages/Update-password';
import AcceptInvite from '../features/auth/pages/AcceptInvite';
import { createLogger } from '../shared/config/logger';
import CustomerPassPage from '../features/customers/pages/CustomerPassPage';
const logger = createLogger('App.js');

export default function App() {
    logger.info('App component initialized');

    const { isAuthenticated, state } = useAuth();
    const { userId, loading: authLoading, loyalpassAdmin } = state;
    const dispatch = useDispatch();

    // Platform admins never use the merchant onboarding tutorial; clear any persisted in-browser state.
    useEffect(() => {
        if (isAuthenticated && loyalpassAdmin) {
            dispatch(resetTutorial());
        }
    }, [isAuthenticated, loyalpassAdmin, dispatch]);

    // Clean up the auth listener when the app component unmounts
    useEffect(() => {
        return () => {
            cleanupAuthListener();
        };
    }, []);

    // Global data fetching after login (skip for platform admins — no merchant tenant)
    useEffect(() => {
        if (isAuthenticated && userId && !loyalpassAdmin) {
            dispatch(rewardsSliceAPI.fetchRewards());
            dispatch(rewardsSliceAPI.fetchAnalytics(userId));
            dispatch(settingsSliceAPI.fetchGeneralSettings());
            dispatch(settingsSliceAPI.fetchRewardSettings());
            dispatch(customersSliceAPI.fetchAllCustomers());
        }
    }, [isAuthenticated, userId, loyalpassAdmin, dispatch]);

    // Wait for initial session check before rendering routes so refresh keeps the current URL
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    return (
        <Router>
            <Routes>
                {/* Routes available to both authenticated and unauthenticated users */}
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />

                {/* Conditional rendering based on authentication status */}
                {!isAuthenticated ? (
                    // Routes for unauthenticated users
                    <>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/confirm" element={<Confirm />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/customer-form" element={<CustomerForm />} />
                        <Route path="/customer/rewards/:passId" element={<CustomerPassPage />} />
                        <Route path="/" element={<Home />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </>
                ) : (
                    // Routes for authenticated users
                    <>
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                        <Route path="/dashboard/*" element={<Dashboard />}>
                            {/* Main dashboard features */}
                            <Route index element={<DashboardHomeRedirect />} />
                            <Route
                                path="loyalpass-admin"
                                element={
                                    <RequirePermission permission="loyalpass_admin">
                                        <LoyalPassAdminLayout />
                                    </RequirePermission>
                                }
                            >
                                <Route index element={<Navigate to="merchants" replace />} />
                                <Route path="merchants" element={<LoyalPassAdminMerchantsPage />} />
                                <Route path="dashboard-previews" element={<LoyalPassAdminDashboardPreviewsIndex />} />
                                <Route
                                    path="dashboard-previews/:merchantId"
                                    element={<LoyalPassAdminDashboardPreviewPage />}
                                />
                                <Route path="merchant-invite" element={<LoyalPassAdminInviteMerchantPage />} />
                                <Route path="merchant-tier" element={<LoyalPassAdminMerchantTierPage />} />
                            </Route>
                            <Route path="customers" element={<RequirePermission permission="view_passes"><Customers /></RequirePermission>} />
                            <Route path="scan" element={<RequirePermission permission="scan_pass"><Scan /></RequirePermission>} />
                            <Route path="generate" element={<RequirePermission permission="generate_pass"><Generate /></RequirePermission>} />
                            <Route path="rewards" element={<RequirePermission permission="view_rewards"><Rewards /></RequirePermission>} />
                            <Route path="marketing" element={<RequirePermission permission="view_marketing"><Marketing /></RequirePermission>} />
                            <Route path="online-presence" element={<OnlinePresence />} />
                            {/* Staff section */}
                            <Route path="staff" element={<RequirePermission permission={['view_staff', 'view_roles']}><StaffLayout /></RequirePermission>}>
                                <Route index element={<StaffIndex />} />
                                <Route path="roles" element={<RequirePermission permission="view_roles"><RolesTab /></RequirePermission>} />
                            </Route>

                            {/* Settings section */}
                            <Route path="settings" element={<RequirePermission permission="view_settings"><SettingsLayout /></RequirePermission>}>
                                <Route path="general" element={<GeneralSettings />} />
                                <Route path="android-pass" element={<AndroidPassSettings />} />
                                <Route path="apple-pass" element={<ApplePassSettings />} />
                                <Route path="global-rewards" element={<RewardsSettings />} />
                                <Route path="membership" element={<Membership />} />
                            </Route>
                        </Route>
                        <Route path="*" element={<Navigate to="/dashboard" />} />
                    </>
                )}
            </Routes>
        </Router>
    );
}
